<script setup lang="ts">
import { onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useProjects } from '@/composables/useApi'
import MemberHeader from "@/components/member/MemberHeader.vue";
import DeveloperSidebar from "@/components/developer/DeveloperSidebar.vue";
import ProjectsTable from '@/components/member/ProjectsTable.vue'

const { t } = useI18n()

// For demo purposes, using a placeholder tenantId
// In a real app, this would come from authentication/user context
const TENANT_ID = '3174acac-c3a7-4276-9ed3-f435a26a7720'

// Use the projects composable
const { projects, loading, error, fetchProjects } = useProjects(TENANT_ID)

// Fetch projects when component mounts
onMounted(async () => {
  await fetchProjects()
})

// Project action handlers
const handleViewProject = (project: any) => {
  console.log('View project:', project.projectName)
  // TODO: Navigate to project detail page
}

const handleEditProject = (project: any) => {
  console.log('Edit project:', project.projectName)
  // TODO: Navigate to project edit page
}

const handleSellSODAs = (project: any) => {
  console.log('Sell SODAs for project:', project.projectName)
  // TODO: Open SODA selling modal or navigate to selling page
}

const handleCreateProject = () => {
  console.log('Create new project')
  // TODO: Navigate to project creation page
}

const handleRefreshProjects = () => {
  fetchProjects()
}
</script>

<template>
  <MemberHeader />

  <div class="flex h-screen bg-gray-50">
    <DeveloperSidebar />
    <main id="main-content">
      <section
        id="explorer-header"
        class="bg-white border-b border-gray-200 py-6"
      >
        <div class="max-w-7xl mx-auto px-5">
          <div class="flex items-center justify-between">
            <div>
              <h1 class="text-2xl font-bold text-accent mb-2">
                {{ $t('developer.browse_registry.title') }}
              </h1>
              <p class="text-gray-600">
                {{ $t('developer.browse_registry.subtitle') }}
              </p>
            </div>
            <div class="flex items-center space-x-3">
              <button
                class="bg-secondary text-accent px-4 py-2 rounded-md text-sm font-medium hover:bg-secondary/90"
              >
                <i class="fas fa-download mr-2"></i>{{ $t('developer.browse_registry.export_report') }}
              </button>
              <button
                class="bg-primary text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-primary/90"
              >
                <i class="fas fa-filter mr-2"></i>{{ $t('developer.browse_registry.advanced_filter') }}
              </button>
            </div>
          </div>
        </div>
      </section>

      <section id="search-filters" class="bg-white py-6">
        <div class="max-w-7xl mx-auto px-5">
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
            <div class="relative">
              <input
                type="text"
                :placeholder="$t('developer.browse_registry.search_placeholder')"
                class="w-full border border-gray-300 rounded-md px-4 py-2 pl-10 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <i class="fas fa-search absolute left-3 top-3 text-gray-400"></i>
            </div>
            <select
              class="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option>{{ $t('developer.browse_registry.filters.all_categories') }}</option>
              <option>Category A</option>
              <option>Category B</option>
              <option>Category C</option>
            </select>
            <select
              class="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option>{{ $t('developer.browse_registry.filters.all_status') }}</option>
              <option>Active</option>
              <option>Under Review</option>
              <option>Registered</option>
              <option>Completed</option>
            </select>
            <select
              class="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option>{{ $t('developer.browse_registry.filters.all_proponents') }}</option>
              <option>GreenTech Solutions</option>
              <option>EcoCarbon Ltd</option>
              <option>Renewable Energy Corp</option>
            </select>
            <select
              class="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option>{{ $t('developer.browse_registry.filters.article6_eligible') }}</option>
              <option>Yes</option>
              <option>No</option>
            </select>
            <button
              class="bg-accent text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-accent/90"
            >
              <i class="fas fa-times mr-2"></i>{{ $t('developer.browse_registry.filters.clear') }}
            </button>
          </div>
        </div>
      </section>

      <section id="stats-overview" class="py-6">
        <div class="max-w-7xl mx-auto px-5">
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            <div
              id="total-projects-stat"
              class="bg-white border border-gray-200 rounded-lg p-6"
            >
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm font-medium text-gray-600">
                    {{ $t('developer.browse_registry.stats.total_projects') }}
                  </p>
                  <p class="text-2xl font-bold text-accent">{{ projects.length }}</p>
                </div>
                <div
                  class="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center"
                >
                  <i class="fas fa-project-diagram text-primary text-xl"></i>
                </div>
              </div>
            </div>

            <div
              id="credits-issued-stat"
              class="bg-white border border-gray-200 rounded-lg p-6"
            >
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm font-medium text-gray-600">
                    {{ $t('developer.browse_registry.stats.credits_issued') }}
                  </p>
                  <p class="text-2xl font-bold text-secondary">2.4M</p>
                </div>
                <div
                  class="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center"
                >
                  <i class="fas fa-certificate text-secondary text-xl"></i>
                </div>
              </div>
            </div>

            <div
              id="credits-retired-stat"
              class="bg-white border border-gray-200 rounded-lg p-6"
            >
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm font-medium text-gray-600">
                    {{ $t('developer.browse_registry.stats.credits_retired') }}
                  </p>
                  <p class="text-2xl font-bold text-accent">847K</p>
                </div>
                <div
                  class="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center"
                >
                  <i class="fas fa-archive text-accent text-xl"></i>
                </div>
              </div>
            </div>

            <div
              id="buffer-credits-stat"
              class="bg-white border border-gray-200 rounded-lg p-6"
            >
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm font-medium text-gray-600">
                    {{ $t('developer.browse_registry.stats.buffer_credits') }}
                  </p>
                  <p class="text-2xl font-bold text-yellow-600">156K</p>
                </div>
                <div
                  class="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center"
                >
                  <i class="fas fa-shield-alt text-yellow-600 text-xl"></i>
                </div>
              </div>
            </div>

            <div
              id="active-transfers-stat"
              class="bg-white border border-gray-200 rounded-lg p-6"
            >
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm font-medium text-gray-600">
                    {{ $t('developer.browse_registry.stats.active_transfers') }}
                  </p>
                  <p class="text-2xl font-bold text-green-600">89</p>
                </div>
                <div
                  class="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center"
                >
                  <i class="fas fa-exchange-alt text-green-600 text-xl"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="projects-table" class="py-6">
        <div class="max-w-7xl mx-auto px-5">
          <!-- Use the shared ProjectsTable component -->
          <ProjectsTable
            :projects="projects"
            :loading="loading"
            :error="error"
            :title="$t('developer.browse_registry.projects_table.title')"
            @refresh="handleRefreshProjects"
            @view-project="handleViewProject"
            @edit-project="handleEditProject"
            @sell-so-das="handleSellSODAs"
            @create-project="handleCreateProject"
          />
        </div>
      </section>

      <section id="credit-tracking" class="py-6">
        <div class="max-w-7xl mx-auto px-5">
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div
              id="credit-transactions"
              class="bg-white border border-gray-200 rounded-lg p-6"
            >
              <h2 class="text-lg font-semibold text-accent mb-4">
                {{ $t('developer.browse_registry.credit_tracking.recent_transactions.title') }}
              </h2>
              <div class="space-y-4">
                <div
                  class="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div class="flex items-center space-x-3">
                    <div
                      class="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center"
                    >
                      <i class="fas fa-arrow-up text-green-600 text-sm"></i>
                    </div>
                    <div>
                      <p class="text-sm font-medium text-gray-900">
                        {{ $t('developer.browse_registry.credit_tracking.recent_transactions.credits_issued') }}
                      </p>
                      <p class="text-xs text-gray-500">SOR-2024-001</p>
                    </div>
                  </div>
                  <div class="text-right">
                    <p class="text-sm font-semibold text-gray-900">+5,230</p>
                    <p class="text-xs text-gray-500">2 hours ago</p>
                  </div>
                </div>

                <div
                  class="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div class="flex items-center space-x-3">
                    <div
                      class="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center"
                    >
                      <i class="fas fa-exchange-alt text-blue-600 text-sm"></i>
                    </div>
                    <div>
                      <p class="text-sm font-medium text-gray-900">
                        {{ $t('developer.browse_registry.credit_tracking.recent_transactions.credits_transferred') }}
                      </p>
                      <p class="text-xs text-gray-500">SOR-2024-002</p>
                    </div>
                  </div>
                  <div class="text-right">
                    <p class="text-sm font-semibold text-gray-900">1,450</p>
                    <p class="text-xs text-gray-500">5 hours ago</p>
                  </div>
                </div>

                <div
                  class="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div class="flex items-center space-x-3">
                    <div
                      class="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center"
                    >
                      <i class="fas fa-minus-circle text-red-600 text-sm"></i>
                    </div>
                    <div>
                      <p class="text-sm font-medium text-gray-900">
                        {{ $t('developer.browse_registry.credit_tracking.recent_transactions.credits_retired') }}
                      </p>
                      <p class="text-xs text-gray-500">SOR-2024-003</p>
                    </div>
                  </div>
                  <div class="text-right">
                    <p class="text-sm font-semibold text-gray-900">-890</p>
                    <p class="text-xs text-gray-500">1 day ago</p>
                  </div>
                </div>
              </div>
            </div>

            <div
              id="export-reports"
              class="bg-white border border-gray-200 rounded-lg p-6"
            >
              <h2 class="text-lg font-semibold text-accent mb-4">
                {{ $t('developer.browse_registry.credit_tracking.generate_reports.title') }}
              </h2>
            </div>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>
